<template>
  <app-drawer
    drawer-ref="reservation-drawer"
    heading="Reservation details"
    :is-open="reservationDrawerStore.isOpen"
    :click-outside-allow-classes="['statements-table', 'reservations-table', 'statement-details-tabs']"
    :is-loading="isPending"
    @close="close"
  >
    <template #body>
      <div class="flex flex-col gap-8">
        <reservation-details-card
          :is-actionable="isActionable"
          :is-loading="isPending"
          @delete:success="close"
          @defer:success="close"
        />

        <reservation-details-identifiers
          :reservation="reservation"
          :is-loading="isPending"
        />

        <reservation-details-pricing
          :reservation="reservation"
          :is-loading="isPending"
        />

        <reservation-details-agency
          :reservation="reservation"
          :is-loading="isPending"
        />

        <reservation-details-notes
          :notes="reservation?.data?.notes || ''"
          :is-loading="isPending"
        />
      </div>
    </template>
  </app-drawer>
</template>

<script setup>
import { computed, provide } from 'vue';
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router';

import { StatementStatus } from '@/constants/statements';
import { useReservationDrawerStore } from '@/stores/reservations';
import { useReservationById } from '@/services/reservations/queries';
import { useStatementById } from '@/services/statements/queries';

import ReservationDetailsCard from '@/components/reservations/ReservationDetails/ReservationDetailsCard.vue';
import ReservationDetailsIdentifiers from '@/components/reservations/ReservationDetails/ReservationDetailsIdentifiers.vue';
import ReservationDetailsAgency from '@/components/reservations/ReservationDetails/ReservationDetailsAgency.vue';
import ReservationDetailsPricing from '@/components/reservations/ReservationDetails/ReservationDetailsPricing.vue';
import ReservationDetailsNotes from '@/components/reservations/ReservationDetails/ReservationDetailsNotes.vue';

const route = useRoute();
const router = useRouter();

const emit = defineEmits(['close']);

const reservationDrawerStore = useReservationDrawerStore();
const reservationId = computed(() => reservationDrawerStore.reservationId);
const statementId = computed(() => route.params.statementId ?? null);

const { data: reservation, isPending } = useReservationById(reservationId);
const { data: statement } = useStatementById(statementId);

provide(
  'statement',
  computed(() => statement.value?.data || {})
);

provide(
  'reservation',
  computed(() => reservation.value?.data || {})
);

const isTransferDetailsRoute = computed(() => route.name === 'transfers-details-reservation');
const isStatementOpened = computed(() => statement.value?.data?.status === StatementStatus.OPENED);
const isActionable = computed(
  () => !isTransferDetailsRoute.value && isStatementOpened.value && !!reservation.value?.data?.id
);

const close = () => {
  switch (route.name) {
    case 'statement-details-reservation':
      router.replace({ name: 'statement-details-reservations', params: { statementId: route.params.statementId } });
      break;
    case 'transfers-details-reservation':
      router.replace({ name: 'transfers-details', params: route.params });
      break;
  }
  reservationDrawerStore.closeDrawer();
  emit('close');
};

onBeforeRouteUpdate((to, _from, next) => {
  if (reservationDrawerStore.isOpen) {
    if (to.name !== 'statement-details-reservation' && to.name !== 'transfers-details-reservation') {
      reservationDrawerStore.closeDrawer();
    }
  }
  next();
});
</script>
